import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Divider,
    CircularProgress,
    Alert,
} from '@mui/material';
import { Image, Tag, apiService } from '../services/api';

interface ImageEditDialogProps {
    open: boolean;
    image: Image | null;
    onClose: () => void;
    onSave: (imageId: number, data: any) => Promise<void>;
}

const ImageEditDialog: React.FC<ImageEditDialogProps> = ({ open, image, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        tagId: '',
        length: '',
        max_thickness: '',
        fully_insertable: false,
        softness: '',
    });
    const [saving, setSaving] = useState(false);
    const [availableTags, setAvailableTags] = useState<Tag[]>([]);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchTags();
    }, []);

    useEffect(() => {
        if (image) {
            setFormData({
                title: image.title || '',
                description: image.description || '',
                tagId: image.primary_tag_id?.toString() || '',
                length: image.length?.toString() || '',
                max_thickness: image.max_thickness?.toString() || '',
                fully_insertable: image.fully_insertable || false,
                softness: image.softness || '',
            });
            setError('');
        }
    }, [image]);

    const fetchTags = async () => {
        try {
            const response = await apiService.getTags();
            setAvailableTags(response.tags || []);
        } catch (err) {
            console.error('Error fetching tags:', err);
        }
    };

    const handleInputChange = (field: string, value: string | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!image) return;
        setSaving(true);
        setError('');
        try {
            await onSave(image.id, {
                title: formData.title,
                description: formData.description,
                length: formData.length,
                max_thickness: formData.max_thickness,
                fully_insertable: formData.fully_insertable,
                softness: formData.softness,
            });

            const newTagId = formData.tagId ? parseInt(formData.tagId) : undefined;
            await apiService.setPrimaryTag(image.id, newTagId);

            onClose();
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to save';
            setError(msg);
            console.error('Error saving image:', err);
        } finally {
            setSaving(false);
        }
    };

    if (!image) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Image</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {error && <Alert severity="error">{error}</Alert>}

                    <TextField
                        fullWidth
                        label="Title"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        size="small"
                    />

                    <TextField
                        fullWidth
                        label="Description"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        multiline
                        rows={3}
                        size="small"
                    />

                    <FormControl fullWidth size="small">
                        <InputLabel>Tag</InputLabel>
                        <Select
                            value={formData.tagId}
                            onChange={(e) => handleInputChange('tagId', e.target.value)}
                            label="Tag"
                        >
                            <MenuItem value=""><em>None</em></MenuItem>
                            {availableTags.map((tag) => (
                                <MenuItem key={tag.id} value={tag.id.toString()}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: tag.color, flexShrink: 0 }} />
                                        {tag.name}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Divider />

                    <Typography variant="subtitle2">Physical Properties</Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <TextField
                            label="Length (cm)"
                            type="number"
                            value={formData.length}
                            onChange={(e) => handleInputChange('length', e.target.value)}
                            inputProps={{ min: 0, step: 0.1 }}
                            size="small"
                        />
                        <TextField
                            label="Max Thickness (cm)"
                            type="number"
                            value={formData.max_thickness}
                            onChange={(e) => handleInputChange('max_thickness', e.target.value)}
                            inputProps={{ min: 0, step: 0.1 }}
                            size="small"
                        />
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Softness</InputLabel>
                            <Select
                                value={formData.softness}
                                onChange={(e) => handleInputChange('softness', e.target.value)}
                                label="Softness"
                            >
                                <MenuItem value="">None</MenuItem>
                                <MenuItem value="very soft">Very Soft</MenuItem>
                                <MenuItem value="soft">Soft</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="firm">Firm</MenuItem>
                                <MenuItem value="very firm">Very Firm</MenuItem>
                            </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">Fully Insertable:</Typography>
                            <Button
                                variant={formData.fully_insertable ? 'contained' : 'outlined'}
                                onClick={() => handleInputChange('fully_insertable', !formData.fully_insertable)}
                                color={formData.fully_insertable ? 'success' : 'primary'}
                                size="small"
                            >
                                {formData.fully_insertable ? 'Yes' : 'No'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} /> : undefined}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ImageEditDialog;
